<template>
  <div class="banner-widget" :class="{ 'banner-widget--reversed': isReversed }">
    <div class="content">
      <p class="t-lead-m" v-html="nbsp(widget.text)" />
      <p class="text t-p-m-160" v-html="nbsp(widget.signature)" />
    </div>
    <div class="image-container">
      <BaseImage
        class="banner-image fill-space"
        :image="widget.image"
        :lazyload="lazyImage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PublicBannerWidget } from '@/service/__generated-api'
import type { Widget } from '@/types/utils'

const props = defineProps<Widget<PublicBannerWidget>>()

const isReversed = computed(() => props.widget.is_flipped)
const backgroundColor = computed(() => props.widget.background_color)
</script>

<style scoped lang="scss">
.banner-widget {
  display: flex;
  flex-direction: column-reverse;

  background-color: v-bind(backgroundColor);

  @include media-up(s-lg) {
    flex-direction: row-reverse;
    min-height: rem(468px);
  }

  &--reversed {
    flex-direction: column;

    @include media-up(s-lg) {
      flex-direction: row;
    }
  }
}

.content {
  @include flex-col;
  gap: rem(32px);
  padding-block: rem(50px);

  @include media-up(s-lg) {
    flex: 1 0 50%;
    justify-content: center;
  }

  @each $breakpoint, $value in $space-around {
    @include media-up($breakpoint) {
      @if ($breakpoint == 'xs' or $breakpoint == 'md') {
        padding-inline-start: $value;
      }
      padding-inline-end: $value;
    }
  }

  .banner-widget--reversed & {
    @each $breakpoint, $value in $space-around {
      @include media-up($breakpoint) {
        @if ($breakpoint == 'md') {
          padding-inline-end: $value;
        }
        padding-inline-start: $value;
      }
    }
  }
}

.text {
  color: $layout-text-02;
}

.image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;

  @include media-up(s-lg) {
    flex: 1 0 50%;
    aspect-ratio: unset;
  }
}
</style>
